<nz-page-header
  nzBackIcon
  (nzBack)="goBack()"
  [nzTitle]="'servicePackages.purchases.title' | translate"
></nz-page-header>

<nz-card>
  <!-- Filter toolbar: search grows to fill, selectors keep a fixed width, and
       Clear is pushed to the right showing the active-filter count. -->
  <form [formGroup]="filterForm" class="flex flex-wrap items-center gap-3 mb-4">
    <nz-input-group [nzPrefix]="searchPrefix" class="flex-1 min-w-[220px]">
      <input
        nz-input
        type="text"
        formControlName="q"
        [placeholder]="'servicePackages.purchases.searchPlaceholder' | translate"
      />
    </nz-input-group>
    <ng-template #searchPrefix>
      <span nz-icon nzType="search" nzTheme="outline" class="text-gray-400"></span>
    </ng-template>

    <beautyapp-select-search
      formControlName="userId"
      [placeholder]="'servicePackages.purchases.filterCustomer' | translate"
      [data]="customers()"
      displayMember="name"
      valueMember="id"
      (onSearch)="onCustomerSearch($event)"
      class="w-[220px]"
    ></beautyapp-select-search>

    <beautyapp-select
      formControlName="packageId"
      [placeholder]="'servicePackages.purchases.filterPackage' | translate"
      [data]="packageOptions()"
      displayMember="label"
      valueMember="value"
      clearable
      class="w-[200px]"
    />

    <beautyapp-select
      formControlName="status"
      [placeholder]="'servicePackages.purchases.filterStatus' | translate"
      [data]="statusOptions()"
      displayMember="label"
      valueMember="value"
      clearable
      class="w-[170px]"
    />

    <nz-range-picker formControlName="dateRange" nzFormat="dd/MM/yyyy"></nz-range-picker>

    <button
      nz-button
      nzType="default"
      class="ml-auto"
      [disabled]="!activeFilterCount()"
      (click)="clearFilters()"
    >
      <span nz-icon nzType="clear" nzTheme="outline"></span>
      {{ 'buttons.clear' | translate }}
      @if (activeFilterCount()) {
        <span
          class="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[11px] leading-none font-semibold"
        >{{ activeFilterCount() }}</span>
      }
    </button>
  </form>

  <!-- Table -->
  <nz-table
    #purchasesTable
    [nzData]="purchases()"
    [nzLoading]="loading()"
    [nzTotal]="total()"
    [nzPageSize]="pageSize()"
    [nzPageIndex]="page()"
    [nzFrontPagination]="false"
    nzShowSizeChanger
    [nzPageSizeOptions]="[10, 20, 50, 100]"
    (nzQueryParams)="onQueryParamsChange($event)"
    nzSize="middle"
    [nzScroll]="{ x: '1400px' }"
  >
    <thead>
      <tr>
        <th>{{ 'servicePackages.purchases.customer' | translate }}</th>
        <th>{{ 'servicePackages.purchases.package' | translate }}</th>
        <th nzWidth="140px" class="whitespace-nowrap">{{ 'servicePackages.purchases.status' | translate }}</th>
        <!-- 240px fits the longest lifecycle tag ("სრულად გამოყენებული") without
             the fixed-layout cell overflowing into the usage column. -->
        <th nzWidth="240px" class="whitespace-nowrap">{{ 'servicePackages.purchases.lifecycle' | translate }}</th>
        <th nzWidth="120px" class="whitespace-nowrap">{{ 'servicePackages.purchases.usage' | translate }}</th>
        <th nzWidth="110px" class="whitespace-nowrap">{{ 'servicePackages.purchases.price' | translate }}</th>
        <th nzWidth="130px" class="whitespace-nowrap">{{ 'servicePackages.purchases.validUntil' | translate }}</th>
        <th nzWidth="120px" nzRight class="whitespace-nowrap">{{ 'servicePackages.columns.actions' | translate }}</th>
      </tr>
    </thead>
    <tbody>
      @for (purchase of purchases(); track purchase.id) {
        <tr>
          <!-- Customer -->
          <td>
            <div class="font-medium">{{ purchase.customer?.name || '—' }}</div>
            @if (purchase.customer?.phone) {
              <div class="text-xs text-gray-500">{{ purchase.customer?.phone }}</div>
            }
            <!-- beaut-251: gift — the customer above is the beneficiary. Compact
                 icon (payer in the tooltip) so it doesn't overflow the column. -->
            @if (purchase.isGift && purchase.payer) {
              <nz-tag
                nzColor="magenta"
                class="mt-1"
                nz-tooltip
                [nzTooltipTitle]="
                  ('services.packages.giftFrom' | translate) + ': ' + purchase.payer.name
                "
              >
                <span nz-icon nzType="gift" nzTheme="outline"></span>
              </nz-tag>
            }
          </td>

          <!-- Package: prefer the snapshot (displayName) over the live-joined
               template name so a template rename does not retroactively
               change what already-sold purchases show. See BE PR #169. -->
          <td>
            {{ purchase.displayName || purchase.package?.name || '—' }}
            @if (purchase.isEdited) {
              <!-- beaut-301 #2: a full text tag collided with the status/lifecycle
                   chips. A compact edit icon (label kept as tooltip) keeps the row clean. -->
              <span
                nz-icon
                nzType="edit"
                nzTheme="outline"
                class="ml-1 text-[#faad14]"
                nz-tooltip
                [nzTooltipTitle]="'servicePackages.purchases.editedBadge' | translate"
              ></span>
            }
          </td>

          <!-- Status -->
          <td>
            <nz-tag [nzColor]="getStatusColor(purchase.status)">
              {{ 'servicePackages.status.' + purchase.status | translate }}
            </nz-tag>
          </td>

          <!-- Lifecycle -->
          <td>
            <nz-tag [nzColor]="purchase | packageLifecycleColor">
              {{ purchase | packageLifecycle | translate }}
            </nz-tag>
          </td>

          <!-- Usage Progress -->
          <td class="whitespace-nowrap">
            <div class="flex items-center gap-2">
              <nz-progress
                [nzPercent]="getUsagePercent(purchase)"
                [nzWidth]="40"
                nzType="circle"
                [nzFormat]="formatProgress"
                nzSize="small"
              ></nz-progress>
              <span class="text-xs text-gray-500">
                {{ purchase.usedServicesCount }}/{{ purchase.totalServicesCount }}
              </span>
            </div>
          </td>

          <!-- Price -->
          <td class="tabular-nums font-medium whitespace-nowrap">{{ purchase.purchasePrice | number:'1.2-2' }} ₾</td>

          <!-- Valid Until -->
          <td>{{ purchase.validUntil | date:'dd/MM/yyyy' }}</td>

          <!-- Actions -->
          <td nzRight>
            <nz-space>
              <button
                *nzSpaceItem
                nz-button
                nzType="text"
                nzSize="small"
                nz-tooltip
                [nzTooltipTitle]="'servicePackages.buttons.details' | translate"
                (click)="viewDetails(purchase)"
              >
                <span nz-icon nzType="eye" nzTheme="outline"></span>
              </button>
              <!-- Edit/cancel only an ACTIVE, not-fully-consumed purchase. A
                   100%-consumed package has nothing left to edit and can't be
                   plain-cancelled (BE rejects it) — so both are hidden. The
                   isFullyUsed() check guards against stored-status drift
                   (a USED package wrongly left ACTIVE). An EXPIRED one is
                   editable too — the modal opens only its term, so the salon
                   can extend it; cancel stays ACTIVE-only. -->
              @if (
                (purchase.status === 'ACTIVE' && !isFullyUsed(purchase)) ||
                purchase.status === 'EXPIRED'
              ) {
                <button
                  *nzSpaceItem
                  nz-button
                  nzType="text"
                  nzSize="small"
                  nz-tooltip
                  [nzTooltipTitle]="'buttons.edit' | translate"
                  [disabled]="editOpening()"
                  (click)="openEditModal(purchase)"
                >
                  <span nz-icon nzType="edit" nzTheme="outline"></span>
                </button>
              }
              @if (purchase.status === 'ACTIVE' && !isFullyUsed(purchase)) {
                <button
                  *nzSpaceItem
                  nz-button
                  nzType="text"
                  nzSize="small"
                  nzDanger
                  nz-tooltip
                  [nzTooltipTitle]="'servicePackages.buttons.cancel' | translate"
                  (click)="cancelPurchase(purchase)"
                >
                  <span nz-icon nzType="close-circle" nzTheme="outline"></span>
                </button>
              }
            </nz-space>
          </td>
        </tr>
      }
      @if (!loading() && !purchases().length) {
        <tr class="purchases-empty">
          <td colspan="8">
            <nz-empty
              [nzNotFoundContent]="
                (activeFilterCount()
                  ? 'servicePackages.purchases.emptyFiltered'
                  : 'servicePackages.purchases.emptyNone'
                ) | translate
              "
            >
              @if (activeFilterCount()) {
                <button nz-button nzType="link" (click)="clearFilters()">
                  {{ 'buttons.clear' | translate }}
                </button>
              }
            </nz-empty>
          </td>
        </tr>
      }
    </tbody>
  </nz-table>
</nz-card>

<ng-template #formatProgress let-percent>
  {{ percent }}%
</ng-template>
